import { useMutation, useQuery } from "convex/react";
import { api } from "convex/_generated/api";
import { Building2, MapPin, User, CheckCircle, XCircle, Search, Trash2 } from "lucide-react";
import { Button } from "../ui/button";
import { useState } from "react";
import type { Id } from "convex/_generated/dataModel";

export function VenueClaimsQueue() {
  const claims = useQuery(api.venues.getPendingVenueClaims);
  const verifyVenueClaim = useMutation(api.venues.verifyVenueClaim);
  const startVenueReview = useMutation(api.venues.startVenueReview);
  const [processing, setProcessing] = useState<string | null>(null);
  // For claims being rejected: track which one is showing reject options
  const [rejectingId, setRejectingId] = useState<string | null>(null);

  const handleStartReview = async (venueId: Id<"venues">) => {
    setProcessing(venueId);
    try {
      await startVenueReview({ venueId });
    } finally {
      setProcessing(null);
    }
  };

  const handleApprove = async (venueId: Id<"venues">) => {
    setProcessing(venueId);
    try {
      await verifyVenueClaim({ venueId, approved: true });
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (venueId: Id<"venues">, deleteVenue: boolean) => {
    setProcessing(venueId);
    try {
      await verifyVenueClaim({ venueId, approved: false, deleteVenue });
      setRejectingId(null);
    } finally {
      setProcessing(null);
    }
  };

  if (claims === undefined) {
    return (
      <div className="space-y-3">
        {[1, 2].map((i) => (
          <div key={i} className="h-24 bg-secondary/30 rounded-lg animate-pulse" />
        ))}
      </div>
    );
  }

  if (claims.length === 0) {
    return (
      <div className="text-center py-12 bg-card rounded-xl border border-border/50">
        <CheckCircle className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
        <p className="text-muted-foreground">No pending venue claims.</p>
      </div>
    );
  }

  const pending = claims.filter((c) => c.claimStatus === "pending");
  const reviewing = claims.filter((c) => c.claimStatus === "reviewing");

  return (
    <div className="space-y-6">
      {/* Under Review */}
      {reviewing.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
            Under Review ({reviewing.length})
          </h3>
          <div className="space-y-3">
            {reviewing.map((claim) => (
              <ClaimCard
                key={claim._id}
                claim={claim}
                isReviewing
                processing={processing}
                rejectingId={rejectingId}
                onApprove={handleApprove}
                onStartReject={() => setRejectingId(claim._id)}
                onCancelReject={() => setRejectingId(null)}
                onReject={handleReject}
              />
            ))}
          </div>
        </div>
      )}

      {/* Pending (in queue) */}
      {pending.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
            In Queue ({pending.length})
          </h3>
          <div className="space-y-3">
            {pending.map((claim) => (
              <ClaimCard
                key={claim._id}
                claim={claim}
                isReviewing={false}
                processing={processing}
                rejectingId={rejectingId}
                onStartReview={handleStartReview}
                onApprove={handleApprove}
                onStartReject={() => setRejectingId(claim._id)}
                onCancelReject={() => setRejectingId(null)}
                onReject={handleReject}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

type Claim = {
  _id: Id<"venues">;
  name: string;
  address: string;
  claimStatus: string;
  ownerName?: string;
  ownerEmail?: string;
  reviewerName?: string;
  description?: string;
};

function ClaimCard({
  claim,
  isReviewing,
  processing,
  rejectingId,
  onStartReview,
  onApprove,
  onStartReject,
  onCancelReject,
  onReject,
}: {
  claim: Claim;
  isReviewing: boolean;
  processing: string | null;
  rejectingId: string | null;
  onStartReview?: (id: Id<"venues">) => void;
  onApprove: (id: Id<"venues">) => void;
  onStartReject: () => void;
  onCancelReject: () => void;
  onReject: (id: Id<"venues">, deleteVenue: boolean) => void;
}) {
  const isProcessing = processing === claim._id;
  const showingRejectOptions = rejectingId === claim._id;

  return (
    <div className={`bg-card rounded-xl border p-5 ${isReviewing ? "border-amber-300 dark:border-amber-700" : "border-border/50"}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Building2 className="w-4 h-4 text-primary flex-shrink-0" />
            <h3 className="font-semibold text-foreground">{claim.name}</h3>
            {isReviewing && (
              <span className="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-2 py-0.5 rounded-full border border-amber-200 dark:border-amber-800">
                Reviewing
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground mb-2">
            <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
            <span>{claim.address}</span>
          </div>
          {(claim.ownerName || claim.ownerEmail) && (
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <User className="w-3.5 h-3.5 flex-shrink-0" />
              <span>
                {claim.ownerName && (
                  <span className="font-medium text-foreground">{claim.ownerName}</span>
                )}
                {claim.ownerName && claim.ownerEmail && " — "}
                {claim.ownerEmail}
              </span>
            </div>
          )}
          {isReviewing && claim.reviewerName && (
            <p className="text-xs text-muted-foreground mt-1">
              Being reviewed by <span className="font-medium">{claim.reviewerName}</span>
            </p>
          )}
          {claim.description && (
            <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
              {claim.description}
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex-shrink-0">
          {!isReviewing && !showingRejectOptions && (
            <Button
              size="sm"
              variant="outline"
              disabled={isProcessing}
              onClick={() => onStartReview?.(claim._id)}
            >
              <Search className="w-4 h-4" />
              Start Review
            </Button>
          )}

          {isReviewing && !showingRejectOptions && (
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={isProcessing}
                onClick={onStartReject}
                className="text-destructive hover:text-destructive border-destructive/30 hover:border-destructive"
              >
                <XCircle className="w-4 h-4" />
                Reject
              </Button>
              <Button
                size="sm"
                disabled={isProcessing}
                onClick={() => onApprove(claim._id)}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                <CheckCircle className="w-4 h-4" />
                Approve
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Rejection options panel */}
      {showingRejectOptions && (
        <div className="mt-4 pt-4 border-t border-border">
          <p className="text-sm font-medium text-foreground mb-3">What should happen to this venue listing?</p>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={isProcessing}
              onClick={() => onReject(claim._id, false)}
            >
              <XCircle className="w-4 h-4" />
              Reject claim only
              <span className="text-xs text-muted-foreground ml-1">(venue stays unclaimed)</span>
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={isProcessing}
              onClick={() => onReject(claim._id, true)}
              className="text-destructive hover:text-destructive border-destructive/30 hover:border-destructive"
            >
              <Trash2 className="w-4 h-4" />
              Reject + remove venue
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={isProcessing}
              onClick={onCancelReject}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
